<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Datenverwaltung</span>
          <h1 class="admin-page-title">Kontakte</h1>
        </div>
      </div>

      <div>
        <!-- Search and Filter Section -->
        <div class="mb-6 bg-white rounded-2xl shadow-lg p-4">
          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-4">
              <div class="flex-1 relative">
                <input v-model="searchQuery" @input="handleSearch" type="text"
                  placeholder="Suche nach Name, NAV-Nr., Adresse, E-Mail, etc..."
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 pl-12 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                <svg class="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none"
                  stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <div class="flex items-center gap-4">
              <span class="text-sm font-semibold text-gray-700">Filter:</span>
              <div class="flex gap-4">
                <div class="">
                  <input type="checkbox" v-model="kreditorFilter" id="kreditorFilter" class="" />
                  <label for="kreditorFilter" class="font-medium"
                    :class="kreditorFilter ? 'text-green-800' : 'text-gray-700'">Kreditor</label>
                </div>
                <div class="">
                  <input type="checkbox" v-model="debitorFilter" id="debitorFilter" class="" />
                  <label for="debitorFilter" class="font-medium"
                    :class="debitorFilter ? 'text-green-800' : 'text-gray-700'">Debitor</label>
                </div>
                <div class="">
                  <input type="checkbox" v-model="subunternehmerFilter" id="subunternehmerFilter" class="" />
                  <label for="subunternehmerFilter" class="font-medium"
                    :class="subunternehmerFilter ? 'text-green-800' : 'text-gray-700'">Subunternehmer</label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Neuer Kontakt Section - Moved to Top -->
        <div v-if="!loading && searchQuery === ''"
          class="mb-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
          <div class="px-6 py-4" style="background-color: #5f656e;">
            <h3 class="text-2xl font-bold text-white">Neuer Kontakt</h3>
          </div>
          <div class="p-6 space-y-6">
            <div class="mb-6">
              <label class="block text-sm font-semibold text-gray-700 mb-3">Typ</label>
              <div class="flex gap-6">
                <input id="new_kreditor" type="checkbox" v-model="newKontakt.is_kreditor"
                  :disabled="newKontakt.is_subunternehmer" @change="onKreditorChange(newKontakt)" class="" />
                <label for="new_kreditor" class="flex items-center gap-3 cursor-pointer">Kreditor
                </label>
                <input id="new_debitor" type="checkbox" v-model="newKontakt.is_debitor" class="" />
                <label for="new_debitor" class="flex items-center gap-3 cursor-pointer">Debitor</label>
                <input id="new_subunternehmer" type="checkbox" v-model="newKontakt.is_subunternehmer"
                  @change="onSubunternehmerChange(newKontakt)" class="" />
                <label for="new_subunternehmer" class="flex items-center gap-3 cursor-pointer">Subunternehmer</label>
              </div>
            </div>
            <div v-if="newKontakt.is_kreditor || newKontakt.is_subunternehmer" class="mb-6">
              <label class="block text-sm font-semibold text-gray-700 mb-3">Erforderliche Dokumente</label>
              <div class="flex flex-wrap gap-6">
                <input id="new_requires_personalausweis" type="checkbox"
                  v-model="newKontakt.requiresPersonalausweis" />
                <label for="new_requires_personalausweis" class="flex items-center gap-3 cursor-pointer">
                  Personalausweis
                </label>
                <input id="new_requires_sv_nachweis" type="checkbox"
                  v-model="newKontakt.requiresSvNachweis" />
                <label for="new_requires_sv_nachweis" class="flex items-center gap-3 cursor-pointer">
                  SV-Nachweis
                </label>
                <input id="new_requires_aufenthaltserlaubnis" type="checkbox"
                  v-model="newKontakt.requiresAufenthaltserlaubnis" />
                <label for="new_requires_aufenthaltserlaubnis" class="flex items-center gap-3 cursor-pointer">
                  Aufenthaltserlaubnis
                </label>
              </div>
            </div>

            <SubunternehmerFields v-if="newKontakt.is_subunternehmer"
              v-model:firmenkuerzel="newKontakt.firmenkuerzel"
              v-model:persnrPrefix="newKontakt.persnrPrefix"
              v-model:bewIdNr="newKontakt.bewIdNr" />
            <div v-if="newKontakt.is_subunternehmer" class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">Basistarife</label>
              <select v-model="newKontakt.subtarifIds" multiple size="4"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                <option v-for="tarif in subtarife" :key="tarif.id" :value="tarif.id">
                  {{ tarif.name }} ({{ formatTarifLaufzeit(tarif) }})
                </option>
              </select>
              <p class="text-xs text-gray-500">Mehrere Tarife mit überschneidender Laufzeit können nicht ausgewählt werden.</p>
            </div>
            <div v-if="newKontakt.is_subunternehmer" class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">Vertragsspezifische Sub-Tarife</label>
              <div v-for="(zuordnung, index) in newKontakt.vertragSubtarife" :key="index" class="flex gap-2">
                <select v-model="zuordnung.vertragId"
                  class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                  <option :value="null">Vertrag wählen…</option>
                  <option v-for="vertrag in vertraege" :key="vertrag.id" :value="vertrag.id">
                    {{ vertrag.vertragsbeschreibung }}
                  </option>
                </select>
                <select v-model="zuordnung.subtarifId"
                  class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                  <option :value="null">Sub-Tarif wählen…</option>
                  <option v-for="tarif in subtarife" :key="tarif.id" :value="tarif.id">
                    {{ tarif.name }} ({{ formatTarifLaufzeit(tarif) }})
                  </option>
                </select>
                <button type="button" @click="newKontakt.vertragSubtarife.splice(index, 1)"
                  class="admin-button--danger admin-button--sm">
                  Entfernen
                </button>
              </div>
              <button type="button" @click="newKontakt.vertragSubtarife.push({ vertragId: null, subtarifId: null })"
                class="admin-button--secondary admin-button--sm">
                Zuordnung hinzufügen
              </button>
              <p class="text-xs text-gray-500">Abweichende Tarife gelten nur für Aufträge des gewählten Vertrags innerhalb der Tarif-Laufzeit.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Unternehmensname *</label>
                <input v-model="newKontakt.unternehmensname"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Unternehmensname" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">NAV-Nr.</label>
                <input v-model="newKontakt.navision_nr"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="NAV-Nr." />
              </div>
              <div></div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Straße</label>
                <input v-model="newKontakt.strasse"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Straße" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Postleitzahl</label>
                <input v-model="newKontakt.postleitzahl"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Postleitzahl" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Stadt</label>
                <input v-model="newKontakt.stadt"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Stadt" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Ländercode</label>
                <input v-model="newKontakt.laendercode"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="z.B. DE" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                <input v-model="newKontakt.website"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Website" />
              </div>
              <div class="md:col-span-2 lg:col-span-3">
                <label class="block text-sm font-semibold text-gray-700 mb-2">Kommentar</label>
                <textarea v-model="newKontakt.kommentar" rows="3"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Kommentar"></textarea>
              </div>
            </div>

            <div class="border-t pt-6">
              <h4 class="text-lg font-semibold text-gray-700 mb-4">Standard-Kontaktpunkt (Kundenservice)</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input v-model="newKontakt.kontaktpunktName"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Kundenservice" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Telefonnummer</label>
                  <input v-model="newKontakt.telefonnummer"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Telefonnummer" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Faxnummer</label>
                  <input v-model="newKontakt.faxnummer"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Faxnummer" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Mobiltelefonnr.</label>
                  <input v-model="newKontakt.mobiltelefonnr"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Mobiltelefonnr." />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">E-Mail</label>
                  <input v-model="newKontakt.email" type="email"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="E-Mail" />
                </div>
              </div>
            </div>

            <div class="flex justify-end pt-2">
              <button type="button" @click="createKontakt"
                :disabled="!newKontakt.unternehmensname.trim() || !isSubunternehmerDataValid(newKontakt)"
                class="admin-button--primary">
                Erstellen
              </button>
            </div>
          </div>
        </div>
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4">
            </div>
            <p class="text-gray-600 font-medium">Lade Kontakte...</p>
          </div>
        </div>

        <!-- Kontakte List -->
        <div v-else class="space-y-6">
          <div v-for="kontakt in filteredKontakte" :key="kontakt.id" :ref="`kontakt-${kontakt.id}`"
            class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
            <div class="px-6 py-4 cursor-pointer flex items-center justify-between" style="background-color: #5f656e;"
              @click="toggleKontakt(kontakt.id)">
              <h3 class="text-xl font-bold text-white">
                {{ kontakt.unternehmensname }}
              </h3>
              <svg class="w-6 h-6 text-white transition-transform duration-300"
                :class="{ 'rotate-180': expandedKontakte.includes(kontakt.id) }" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <div v-show="expandedKontakte.includes(kontakt.id)" class="p-6">
              <div v-if="editingKontakt === kontakt.id" class="space-y-6">
                <div class="mb-6">
                  <label class="block text-sm font-semibold text-gray-700 mb-3">Typ</label>
                  <div class="flex gap-6">
                    <div class="flex gap-6">
                      <input :id="'kreditor-' + kontakt.id" type="checkbox" v-model="editKontaktData.is_kreditor"
                        :disabled="editKontaktData.is_subunternehmer"
                        @change="onKreditorChange(editKontaktData)" class="" />
                      <label :for="'kreditor-' + kontakt.id" class="flex items-center gap-3 cursor-pointer">Kreditor
                      </label>
                      <input :id="'debitor-' + kontakt.id" type="checkbox" v-model="editKontaktData.is_debitor"
                        class="" />
                      <label :for="'debitor-' + kontakt.id"
                        class="flex items-center gap-3 cursor-pointer">Debitor</label>
                      <input :id="'subunternehmer-' + kontakt.id" type="checkbox"
                        v-model="editKontaktData.is_subunternehmer" @change="onSubunternehmerChange(editKontaktData)"
                        class="" />
                      <label :for="'subunternehmer-' + kontakt.id"
                        class="flex items-center gap-3 cursor-pointer">Subunternehmer</label>
                    </div>
                  </div>
                </div>
                <div v-if="editKontaktData.is_kreditor || editKontaktData.is_subunternehmer" class="mb-6">
                  <label class="block text-sm font-semibold text-gray-700 mb-3">Erforderliche Dokumente</label>
                  <div class="flex flex-wrap gap-6">
                    <input :id="'requires_personalausweis-' + kontakt.id" type="checkbox"
                      v-model="editKontaktData.requiresPersonalausweis" />
                    <label :for="'requires_personalausweis-' + kontakt.id"
                      class="flex items-center gap-3 cursor-pointer">
                      Personalausweis
                    </label>
                    <input :id="'requires_sv_nachweis-' + kontakt.id" type="checkbox"
                      v-model="editKontaktData.requiresSvNachweis" />
                    <label :for="'requires_sv_nachweis-' + kontakt.id"
                      class="flex items-center gap-3 cursor-pointer">
                      SV-Nachweis
                    </label>
                    <input :id="'requires_aufenthaltserlaubnis-' + kontakt.id" type="checkbox"
                      v-model="editKontaktData.requiresAufenthaltserlaubnis" />
                    <label :for="'requires_aufenthaltserlaubnis-' + kontakt.id"
                      class="flex items-center gap-3 cursor-pointer">
                      Aufenthaltserlaubnis
                    </label>
                  </div>
                </div>

                <SubunternehmerFields v-if="editKontaktData.is_subunternehmer"
                  v-model:firmenkuerzel="editKontaktData.firmenkuerzel"
                  v-model:persnrPrefix="editKontaktData.persnrPrefix"
                  v-model:bewIdNr="editKontaktData.bewIdNr" />
                <div v-if="editKontaktData.is_subunternehmer" class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Basistarife</label>
                  <select v-model="editKontaktData.subtarifIds" multiple size="4"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                    <option v-for="tarif in subtarife" :key="tarif.id" :value="tarif.id">
                      {{ tarif.name }} ({{ formatTarifLaufzeit(tarif) }})
                    </option>
                  </select>
                  <p class="text-xs text-gray-500">Mehrere Tarife mit überschneidender Laufzeit können nicht ausgewählt werden.</p>
                </div>
                <div v-if="editKontaktData.is_subunternehmer" class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Vertragsspezifische Sub-Tarife</label>
                  <div v-for="(zuordnung, index) in editKontaktData.vertragSubtarife" :key="index" class="flex gap-2">
                    <select v-model="zuordnung.vertragId"
                      class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                      <option :value="null">Vertrag wählen…</option>
                      <option v-for="vertrag in vertraege" :key="vertrag.id" :value="vertrag.id">
                        {{ vertrag.vertragsbeschreibung }}
                      </option>
                    </select>
                    <select v-model="zuordnung.subtarifId"
                      class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                      <option :value="null">Sub-Tarif wählen…</option>
                      <option v-for="tarif in subtarife" :key="tarif.id" :value="tarif.id">
                        {{ tarif.name }} ({{ formatTarifLaufzeit(tarif) }})
                      </option>
                    </select>
                    <button type="button" @click="editKontaktData.vertragSubtarife.splice(index, 1)"
                      class="admin-button--danger admin-button--sm">
                      Entfernen
                    </button>
                  </div>
                  <button type="button" @click="editKontaktData.vertragSubtarife.push({ vertragId: null, subtarifId: null })"
                    class="admin-button--secondary admin-button--sm">
                    Zuordnung hinzufügen
                  </button>
                  <p class="text-xs text-gray-500">Abweichende Tarife gelten nur für Aufträge des gewählten Vertrags innerhalb der Tarif-Laufzeit.</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Unternehmensname *</label>
                    <input v-model="editKontaktData.unternehmensname"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">NAV-Nr.</label>
                    <input v-model="editKontaktData.navision_nr"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div></div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Straße</label>
                    <input v-model="editKontaktData.strasse"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Postleitzahl</label>
                    <input v-model="editKontaktData.postleitzahl"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Stadt</label>
                    <input v-model="editKontaktData.stadt"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Ländercode</label>
                    <input v-model="editKontaktData.laendercode"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                    <input v-model="editKontaktData.website"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
                  </div>
                  <div class="md:col-span-2 lg:col-span-3">
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Kommentar</label>
                    <textarea v-model="editKontaktData.kommentar" rows="3"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"></textarea>
                  </div>
                </div>


                <div class="flex gap-3 justify-end">
                  <button type="button" @click="cancelEdit"
                    class="admin-button--secondary">
                    Abbrechen
                  </button>
                  <button type="button" @click="saveKontakt(kontakt.id)"
                    class="admin-button--primary">
                    Speichern
                  </button>
                </div>
              </div>

              <div v-else class="space-y-6">
                <div class="mb-4 flex gap-2">
                  <span v-if="kontakt.is_kreditor"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                    Kreditor
                  </span>
                  <span v-if="kontakt.is_debitor"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    Debitor
                  </span>
                  <span v-if="kontakt.is_subunternehmer"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                    Subunternehmer<template v-if="kontakt.firmenkuerzel"> ({{ kontakt.firmenkuerzel }})</template>
                  </span>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div v-if="kontakt.navision_nr">
                    <p class="text-sm font-semibold text-gray-500">NAV-Nr.</p>
                    <p class="text-gray-800">{{ kontakt.navision_nr }}</p>
                  </div>
                  <div v-if="kontakt.firmenkuerzel">
                    <p class="text-sm font-semibold text-gray-500">Firmenkürzel</p>
                    <p class="text-gray-800">{{ kontakt.firmenkuerzel }}</p>
                  </div>
                  <div v-if="kontakt.bewIdNr">
                    <p class="text-sm font-semibold text-gray-500">Bew-Id-Nr.</p>
                    <p class="text-gray-800">{{ kontakt.bewIdNr }}</p>
                  </div>
                  <div v-if="kontakt.strasse">
                    <p class="text-sm font-semibold text-gray-500">Straße</p>
                    <p class="text-gray-800">{{ kontakt.strasse }}</p>
                  </div>
                  <div v-if="kontakt.postleitzahl || kontakt.stadt">
                    <p class="text-sm font-semibold text-gray-500">PLZ / Stadt</p>
                    <p class="text-gray-800">{{ kontakt.postleitzahl }} {{ kontakt.stadt }}</p>
                  </div>
                  <div v-if="kontakt.website">
                    <p class="text-sm font-semibold text-gray-500">Website</p>
                    <a :href="kontakt.website" target="_blank" class="text-blue-600 hover:underline">{{ kontakt.website
                    }}</a>
                  </div>
                </div>

                <div v-if="kontakt.kommentar" class="border-t pt-4">
                  <p class="text-sm font-semibold text-gray-500 mb-2">Kommentar</p>
                  <p class="text-gray-800 whitespace-pre-wrap">{{ kontakt.kommentar }}</p>
                </div>

                <div v-if="kontakt.is_subunternehmer || (kontakt.subtarifIds && kontakt.subtarifIds.length)" class="border-t pt-4">
                  <h4 class="text-lg font-semibold text-gray-700 mb-3">Basistarife</h4>
                  <p v-if="kontakt.subtarifIds && kontakt.subtarifIds.length" class="text-gray-800">
                    {{ kontaktSubtarifNames(kontakt).join(', ') }}
                  </p>
                  <p v-else class="text-sm text-gray-500">Keine Basistarife zugeordnet</p>
                  <div v-if="kontakt.vertragSubtarife && kontakt.vertragSubtarife.length" class="mt-3">
                    <p class="text-sm font-semibold text-gray-500 mb-1">Vertragsspezifische Sub-Tarife</p>
                    <ul class="text-sm text-gray-800 space-y-1">
                      <li v-for="(zuordnung, index) in kontakt.vertragSubtarife" :key="index">
                        {{ zuordnung.vertragsbeschreibung }} → {{ zuordnung.subtarifName }}
                        <span class="text-gray-500">({{ formatDate(zuordnung.laufzeitVon) }} - {{ formatDate(zuordnung.laufzeitBis) }})</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="border-t pt-6">
                  <div class="flex items-center justify-between mb-4">
                    <h4 class="text-lg font-semibold text-gray-700">Kontaktpunkte</h4>
                    <button type="button" @click="addKontaktpunkt(kontakt.id)"
                      class="admin-button--primary admin-button--sm">
                      Hinzufügen
                    </button>
                  </div>

                  <div v-if="kontakt.kontaktpunkte && kontakt.kontaktpunkte.length > 0" class="space-y-3">
                    <div v-for="kp in kontakt.kontaktpunkte" :key="kp.id"
                      class="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div class="flex items-start justify-between">
                        <div class="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          <div>
                            <p class="text-sm font-semibold text-gray-500">Name</p>
                            <div class="flex items-center gap-2">
                              <p class="text-gray-800">{{ kp.name }}</p>
                              <span v-if="kp.is_default"
                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                                Standard
                              </span>
                            </div>
                          </div>
                          <div v-if="kp.telefonnummer">
                            <p class="text-sm font-semibold text-gray-500">Telefon</p>
                            <a :href="'tel:' + kp.telefonnummer" class="text-blue-600 hover:underline">{{
                              kp.telefonnummer }}</a>
                          </div>
                          <div v-if="kp.faxnummer">
                            <p class="text-sm font-semibold text-gray-500">Fax</p>
                            <a :href="'tel:' + kp.faxnummer" class="text-blue-600 hover:underline">{{ kp.faxnummer
                            }}</a>
                          </div>
                          <div v-if="kp.mobiltelefonnr">
                            <p class="text-sm font-semibold text-gray-500">Mobil</p>
                            <a :href="'tel:' + kp.mobiltelefonnr" class="text-blue-600 hover:underline">{{
                              kp.mobiltelefonnr }}</a>
                          </div>
                          <div v-if="kp.email">
                            <p class="text-sm font-semibold text-gray-500">E-Mail</p>
                            <a :href="'mailto:' + kp.email" class="text-blue-600 hover:underline">{{ kp.email }}</a>
                          </div>
                        </div>
                        <div class="flex gap-2 ml-4">
                          <button type="button" @click="setDefaultKontaktpunkt(kontakt.id, kp.id)"
                            :disabled="kp.is_default"
                            class="admin-button--ghost admin-button--icon admin-button--sm"
                            title="Als Standard setzen" aria-label="Als Standard setzen">
                            <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          </button>
                          <button type="button" @click="editKontaktpunkt(kontakt.id, kp)" class="admin-button--ghost admin-button--icon admin-button--sm" title="Bearbeiten" aria-label="Kontaktpunkt bearbeiten">
                            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button type="button" @click="deleteKontaktpunkt(kontakt.id, kp.id)"
                            class="admin-button--ghost admin-button--icon admin-button--sm" title="Löschen" aria-label="Kontaktpunkt löschen">
                            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-8 text-gray-500">
                    <p>Keine Kontaktpunkte vorhanden</p>
                  </div>
                </div>

                <div class="border-t pt-6">
                  <div class="flex items-center justify-between mb-4">
                    <h4 class="text-lg font-semibold text-gray-700">Dokumente</h4>
                    <label
                      class="admin-button admin-button--primary admin-button--sm cursor-pointer">
                      <input type="file" @change="uploadDocument(kontakt.id, $event)" class="hidden" />
                      Hochladen
                    </label>
                  </div>

                  <div v-if="kontakt.documents && kontakt.documents.length > 0" class="space-y-2">
                    <div v-for="doc in kontakt.documents" :key="doc.id"
                      class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div class="flex items-center gap-3 flex-1">
                        <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-900 truncate">{{ doc.originalName }}</p>
                          <p class="text-xs text-gray-500">{{ formatFileSize(doc.size) }} • {{ formatDate(doc.createdAt)
                            }}</p>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <button type="button" @click="downloadDocument(kontakt.id, doc.id)"
                          class="admin-button--ghost admin-button--icon admin-button--sm"
                          title="Herunterladen" aria-label="Dokument herunterladen">
                          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                        <button type="button" @click="deleteDocument(kontakt.id, doc.id)"
                          class="admin-button--ghost admin-button--icon admin-button--sm"
                          title="Löschen" aria-label="Dokument löschen">
                          <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-8 text-gray-500">
                    <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p>Keine Dokumente vorhanden</p>
                  </div>
                </div>

                <div class="flex gap-3 justify-end pt-4 border-t">
                  <button type="button" @click="startEdit(kontakt)"
                    class="admin-button--secondary">
                    Bearbeiten
                  </button>
                  <button type="button" @click="deleteKontakt(kontakt.id)"
                    class="admin-button--danger">
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="kontakte.length === 0" class="text-center py-16">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p class="text-gray-500 text-lg font-medium">Keine Kontakte vorhanden</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Kontaktpunkt Modal -->
    <div v-if="showKontaktpunktModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeKontaktpunktModal">
      <div class="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-4 border-b" style="background-color: #5f656e;">
          <h3 class="text-2xl font-bold text-white">{{ editingKontaktpunkt ? 'Kontaktpunkt bearbeiten' : 'Kontaktpunkt hinzufügen' }}</h3>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
            <input v-model="editKontaktpunktData.name"
              class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
              placeholder="z.B. Kundenservice" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Telefonnummer</label>
              <input v-model="editKontaktpunktData.telefonnummer"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                placeholder="Telefonnummer" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Faxnummer</label>
              <input v-model="editKontaktpunktData.faxnummer"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                placeholder="Faxnummer" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Mobiltelefonnr.</label>
              <input v-model="editKontaktpunktData.mobiltelefonnr"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                placeholder="Mobiltelefonnr." />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">E-Mail</label>
              <input v-model="editKontaktpunktData.email" type="email"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                placeholder="E-Mail" />
            </div>
          </div>
          <div class="flex items-center gap-3">
            <input type="checkbox" v-model="editKontaktpunktData.is_default" id="modal_is_default" class="" />
            <label for="modal_is_default" class="text-sm font-semibold text-gray-700 cursor-pointer">Als Standard-Kontaktpunkt setzen</label>
          </div>
        </div>
        <div class="px-6 py-4 border-t bg-gray-50 flex gap-3 justify-end">
          <button type="button" @click="closeKontaktpunktModal"
            class="admin-button--secondary">
            Abbrechen
          </button>
          <button type="button" @click="saveKontaktpunkt" :disabled="!editKontaktpunktData.name.trim()"
            class="admin-button--primary">
            {{ editingKontaktpunkt ? 'Speichern' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from '../../CASP';
import SubunternehmerFields from '../../components/admin/SubunternehmerFields.vue';

export default {
  components: { SubunternehmerFields },
  data() {
    return {
      loading: true,
      searchQuery: '',
      searchTimeout: null,
      kreditorFilter: false,
      debitorFilter: false,
      subunternehmerFilter: false,
      kontakte: [],
      subtarife: [],
      vertraege: [],
      expandedKontakte: [],
      editingKontakt: null,
      newKontakt: {
        is_kreditor: false,
        is_debitor: false,
        is_subunternehmer: false,
        requiresPersonalausweis: false,
        requiresSvNachweis: false,
        requiresAufenthaltserlaubnis: false,
        firmenkuerzel: '',
        persnrPrefix: '',
        bewIdNr: '',
        subtarifIds: [],
        vertragSubtarife: [],
        navision_nr: '',
        unternehmensname: '',
        strasse: '',
        postleitzahl: '',
        stadt: '',
        laendercode: '',
        website: '',
        kommentar: '',
        kontaktpunktName: '',
        telefonnummer: '',
        faxnummer: '',
        mobiltelefonnr: '',
        email: ''
      },
      editKontaktData: {
        is_kreditor: false,
        is_debitor: false,
        is_subunternehmer: false,
        requiresPersonalausweis: false,
        requiresSvNachweis: false,
        requiresAufenthaltserlaubnis: false,
        firmenkuerzel: '',
        persnrPrefix: '',
        bewIdNr: '',
        subtarifIds: [],
        vertragSubtarife: [],
        navision_nr: '',
        unternehmensname: '',
        strasse: '',
        postleitzahl: '',
        stadt: '',
        laendercode: '',
        website: '',
        kommentar: ''
      },
      showKontaktpunktModal: false,
      editingKontaktpunkt: null,
      currentKontaktId: null,
      editKontaktpunktData: {
        name: '',
        telefonnummer: '',
        faxnummer: '',
        mobiltelefonnr: '',
        email: '',
        is_default: false
      }
    };
  },
  computed: {
    filteredKontakte() {

      let returnlist = this.kontakte.filter(kontakt => {
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();

          if (kontakt.unternehmensname?.toLowerCase().includes(query)) return true;
          if (kontakt.navision_nr?.toLowerCase().includes(query)) return true;
          if (kontakt.strasse?.toLowerCase().includes(query)) return true;
          if (kontakt.postleitzahl?.toLowerCase().includes(query)) return true;
          if (kontakt.stadt?.toLowerCase().includes(query)) return true;
          if (kontakt.website?.toLowerCase().includes(query)) return true;
          if (kontakt.firmenkuerzel?.toLowerCase().includes(query)) return true;

          if (kontakt.kontaktpunkte && kontakt.kontaktpunkte.length > 0) {
            return kontakt.kontaktpunkte.some(kp =>
              kp.name?.toLowerCase().includes(query) ||
              kp.email?.toLowerCase().includes(query) ||
              kp.telefonnummer?.toLowerCase().includes(query) ||
              kp.faxnummer?.toLowerCase().includes(query) ||
              kp.mobiltelefonnr?.toLowerCase().includes(query)
            );
          }
          return false;
        }
        // Each checked type filter must match
        if (this.kreditorFilter && !kontakt.is_kreditor) return false;
        if (this.debitorFilter && !kontakt.is_debitor) return false;
        if (this.subunternehmerFilter && !kontakt.is_subunternehmer) return false;
        return true;
      });
      if (returnlist.length === 1) {
        this.expandedKontakte.push(returnlist[0].id);
      }
      else {
        this.expandedKontakte = [];
      }
      return returnlist;
    }
  },
  methods: {
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // this.filterData();
      }, 300);
    },

    loadData() {
      this.loading = true;

      CASP.getKontakte(null, "", (status, response) => {
        if (status === 200) {
          this.kontakte = response.map(k => ({
            ...k,
            documents: k.documents || []
          }));
          this.loading = false;
        } else {
          console.error('Fehler beim Laden der Kontakte:', response);
          alert('Fehler beim Laden der Kontakte: ' + (response?.error || response));
          this.filterData();
          this.loading = false;
        }
      });
    },
    loadSubtarife() {
      CASP.getTarife((status, response) => {
        if (status === 200) {
          this.subtarife = (response.subtarife || []).map(tarif => ({
            ...tarif,
            categoryPrices: tarif.categoryPrices || {}
          }));
        } else {
          console.error('Fehler beim Laden der Sub-Tarife:', response);
        }
      });
    },
    loadVertraege() {
      CASP.getVertraege((status, response) => {
        if (status === 200) {
          this.vertraege = (response || []).map(vertrag => ({
            id: vertrag.id,
            vertragsbeschreibung: vertrag.vertragsbeschreibung
          }));
        } else {
          console.error('Fehler beim Laden der Verträge:', response);
        }
      });
    },
    onSubunternehmerChange(form) {
      // Subunternehmer sind immer auch Kreditoren
      if (form.is_subunternehmer) {
        form.is_kreditor = true;
      } else if (!form.is_kreditor) {
        this.clearDocumentRequirements(form);
      }
    },
    onKreditorChange(form) {
      if (!form.is_kreditor && !form.is_subunternehmer) {
        this.clearDocumentRequirements(form);
      }
    },
    clearDocumentRequirements(form) {
      form.requiresPersonalausweis = false;
      form.requiresSvNachweis = false;
      form.requiresAufenthaltserlaubnis = false;
    },
    isSubunternehmerDataValid(form) {
      if (!form.is_subunternehmer) return true;
      return !!(form.firmenkuerzel && form.firmenkuerzel.trim());
    },
    formatTarifLaufzeit(tarif) {
      return `${this.formatDate(tarif.laufzeitVon)} - ${this.formatDate(tarif.laufzeitBis)}`;
    },
    kontaktSubtarifNames(kontakt) {
      const ids = (kontakt.subtarifIds || []).map(Number);
      return ids.map(id => {
        const tarif = this.subtarife.find(item => item.id === id);
        return tarif ? tarif.name : `Sub-Tarif #${id}`;
      });
    },
    toggleKontakt(id) {
      const index = this.expandedKontakte.indexOf(id);
      if (index > -1) {
        this.expandedKontakte.splice(index, 1);
      } else {
        this.expandedKontakte.push(id);
      }
    },
    scrollToKontakt(id) {
      this.$nextTick(() => {
        const element = this.$refs[`kontakt-${id}`];
        if (element && element[0]) {
          element[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    createKontakt() {
      if (!this.newKontakt.unternehmensname.trim()) return;
      if (!this.isSubunternehmerDataValid(this.newKontakt)) {
        alert('Bitte ein Firmenkürzel für den Subunternehmer angeben.');
        return;
      }

      const kontaktData = {
        ...this.newKontakt,
        subtarifIds: this.newKontakt.is_subunternehmer ? this.newKontakt.subtarifIds : [],
        vertragSubtarife: this.newKontakt.is_subunternehmer
          ? this.newKontakt.vertragSubtarife.filter(z => z.vertragId && z.subtarifId)
          : []
      };
      CASP.createKontakt(kontaktData, (status, response) => {
        if (status === 200) {
          this.newKontakt = {
            is_kreditor: false,
            is_debitor: false,
            is_subunternehmer: false,
            requiresPersonalausweis: false,
            requiresSvNachweis: false,
            requiresAufenthaltserlaubnis: false,
            firmenkuerzel: '',
            persnrPrefix: '',
            subtarifIds: [],
            vertragSubtarife: [],
            navision_nr: '',
            unternehmensname: '',
            strasse: '',
            postleitzahl: '',
            stadt: '',
            laendercode: '',
            website: '',
            kommentar: '',
            kontaktpunktName: '',
            telefonnummer: '',
            faxnummer: '',
            mobiltelefonnr: '',
            email: ''
          };
          this.loadData();
        } else {
          console.error('Fehler beim Erstellen des Kontakts:', response);
          alert('Fehler beim Erstellen des Kontakts: ' + (response?.error || response));
        }
      });
    },
    startEdit(kontakt) {
      this.editingKontakt = kontakt.id;
      this.editKontaktData = {
        is_kreditor: kontakt.is_kreditor || false,
        is_debitor: kontakt.is_debitor || false,
        is_subunternehmer: kontakt.is_subunternehmer || false,
        requiresPersonalausweis: kontakt.requiresPersonalausweis || false,
        requiresSvNachweis: kontakt.requiresSvNachweis || false,
        requiresAufenthaltserlaubnis: kontakt.requiresAufenthaltserlaubnis || false,
        firmenkuerzel: kontakt.firmenkuerzel || '',
        persnrPrefix: kontakt.persnrPrefix || '',
        bewIdNr: kontakt.bewIdNr || '',
        subtarifIds: (kontakt.subtarifIds || []).map(Number),
        vertragSubtarife: (kontakt.vertragSubtarife || []).map(zuordnung => ({
          vertragId: zuordnung.vertragId,
          subtarifId: zuordnung.subtarifId
        })),
        navision_nr: kontakt.navision_nr || '',
        unternehmensname: kontakt.unternehmensname || '',
        strasse: kontakt.strasse || '',
        postleitzahl: kontakt.postleitzahl || '',
        stadt: kontakt.stadt || '',
        laendercode: kontakt.laendercode || '',
        website: kontakt.website || '',
        kommentar: kontakt.kommentar || ''
      };
    },
    cancelEdit() {
      this.editingKontakt = null;
      this.editKontaktData = {
        is_kreditor: false,
        is_debitor: false,
        is_subunternehmer: false,
        requiresPersonalausweis: false,
        requiresSvNachweis: false,
        requiresAufenthaltserlaubnis: false,
        firmenkuerzel: '',
        persnrPrefix: '',
        bewIdNr: '',
        subtarifIds: [],
        vertragSubtarife: [],
        navision_nr: '',
        unternehmensname: '',
        strasse: '',
        postleitzahl: '',
        stadt: '',
        laendercode: '',
        website: '',
        kommentar: ''
      };
    },
    saveKontakt(id) {
      if (!this.isSubunternehmerDataValid(this.editKontaktData)) {
        alert('Bitte ein Firmenkürzel für den Subunternehmer angeben.');
        return;
      }
      const kontaktData = {
        ...this.editKontaktData,
        vertragSubtarife: this.editKontaktData.is_subunternehmer
          ? this.editKontaktData.vertragSubtarife.filter(z => z.vertragId && z.subtarifId)
          : []
      };

      CASP.updateKontakt(id, kontaktData, (status, response) => {
        if (status === 200) {
          this.editingKontakt = null;
          this.loadData();
        } else {
          console.error('Fehler beim Aktualisieren des Kontakts:', response);
          alert('Fehler beim Aktualisieren des Kontakts: ' + (response?.error || response));
        }
      });
    },
    deleteKontakt(id) {
      if (!confirm('Möchten Sie diesen Kontakt wirklich löschen?')) return;

      CASP.deleteKontakt(id, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Kontakts:', response);
          alert('Fehler beim Löschen des Kontakts: ' + (response?.error || response));
        }
      });
    },
    uploadDocument(kontaktId, event) {
      const file = event.target.files[0];
      if (!file) return;

      const fileToUpload = new File([file], file.name, { type: file.type });

      event.target.value = '';

      CASP.uploadKontaktDocument(kontaktId, fileToUpload, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Hochladen des Dokuments:', response);
          alert('Fehler beim Hochladen des Dokuments: ' + (response?.error || response));
        }
      });
    },
    downloadDocument(kontaktId, documentId) {
      CASP.downloadKontaktDocument(kontaktId, documentId);
    },
    deleteDocument(kontaktId, documentId) {
      if (!confirm('Möchten Sie dieses Dokument wirklich löschen?')) return;

      CASP.deleteKontaktDocument(kontaktId, documentId, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Dokuments:', response);
          alert('Fehler beim Löschen des Dokuments: ' + (response?.error || response));
        }
      });
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE');
    },
    addKontaktpunkt(kontaktId) {
      this.currentKontaktId = kontaktId;
      this.editingKontaktpunkt = null;
      this.editKontaktpunktData = {
        name: 'Kundenservice',
        telefonnummer: '',
        faxnummer: '',
        mobiltelefonnr: '',
        email: '',
        is_default: false
      };
      this.showKontaktpunktModal = true;
    },
    editKontaktpunkt(kontaktId, kontaktpunkt) {
      this.currentKontaktId = kontaktId;
      this.editingKontaktpunkt = kontaktpunkt.id;
      this.editKontaktpunktData = {
        name: kontaktpunkt.name || '',
        telefonnummer: kontaktpunkt.telefonnummer || '',
        faxnummer: kontaktpunkt.faxnummer || '',
        mobiltelefonnr: kontaktpunkt.mobiltelefonnr || '',
        email: kontaktpunkt.email || '',
        is_default: kontaktpunkt.is_default || false
      };
      this.showKontaktpunktModal = true;
    },
    saveKontaktpunkt() {
      if (!this.editKontaktpunktData.name.trim()) return;

      const kontaktpunktData = {
        name: this.editKontaktpunktData.name,
        telefonnummer: this.editKontaktpunktData.telefonnummer || null,
        faxnummer: this.editKontaktpunktData.faxnummer || null,
        mobiltelefonnr: this.editKontaktpunktData.mobiltelefonnr || null,
        email: this.editKontaktpunktData.email || null,
        is_default: this.editKontaktpunktData.is_default || false
      };

      if (this.editingKontaktpunkt) {
        CASP.updateKontaktpunkt(this.currentKontaktId, this.editingKontaktpunkt, kontaktpunktData, (status, response) => {
          if (status === 200) {
            this.closeKontaktpunktModal();
            this.loadData();
          } else {
            console.error('Fehler beim Aktualisieren des Kontaktpunkts:', response);
            alert('Fehler beim Aktualisieren des Kontaktpunkts: ' + (response?.error || response));
          }
        });
      } else {
        CASP.createKontaktpunkt(this.currentKontaktId, kontaktpunktData, (status, response) => {
          if (status === 200) {
            this.closeKontaktpunktModal();
            this.loadData();
          } else {
            console.error('Fehler beim Erstellen des Kontaktpunkts:', response);
            alert('Fehler beim Erstellen des Kontaktpunkts: ' + (response?.error || response));
          }
        });
      }
    },
    closeKontaktpunktModal() {
      this.showKontaktpunktModal = false;
      this.editingKontaktpunkt = null;
      this.currentKontaktId = null;
      this.editKontaktpunktData = {
        name: '',
        telefonnummer: '',
        faxnummer: '',
        mobiltelefonnr: '',
        email: '',
        is_default: false
      };
    },
    setDefaultKontaktpunkt(kontaktId, kontaktpunktId) {
      const kontakt = this.kontakte.find(k => k.id === kontaktId);
      if (!kontakt) return;
      
      const kontaktpunkt = kontakt.kontaktpunkte.find(kp => kp.id === kontaktpunktId);
      if (!kontaktpunkt) return;

      const kontaktpunktData = {
        name: kontaktpunkt.name,
        telefonnummer: kontaktpunkt.telefonnummer || null,
        faxnummer: kontaktpunkt.faxnummer || null,
        mobiltelefonnr: kontaktpunkt.mobiltelefonnr || null,
        email: kontaktpunkt.email || null,
        is_default: true
      };

      CASP.updateKontaktpunkt(kontaktId, kontaktpunktId, kontaktpunktData, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Setzen des Standard-Kontaktpunkts:', response);
          alert('Fehler beim Setzen des Standard-Kontaktpunkts: ' + (response?.error || response));
        }
      });
    },
    deleteKontaktpunkt(kontaktId, kontaktpunktId) {
      if (!confirm('Möchten Sie diesen Kontaktpunkt wirklich löschen?')) return;

      CASP.deleteKontaktpunkt(kontaktId, kontaktpunktId, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Kontaktpunkts:', response);
          alert('Fehler beim Löschen des Kontaktpunkts: ' + (response?.error || response));
        }
      });
    }
  },
  mounted() {
    this.loadData();
    this.loadSubtarife();
    this.loadVertraege();
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
  }
};
</script>
